export interface IkCsvWriteOptions {
    QuoteAlso?: string;
    AlwaysQuote?: boolean;
    GuardFormulas?: boolean;
}

/** CSV/TSV records without I/O, error gates or host policy. Callers own malformed-input faults. */
export class kCsvCore {
    /** DOCUMENTED EXCEPTION: pure cell writer; quoting and spreadsheet formula policy are explicit. */
    public static Cell = (Value: string, Delimiter: string = ",", Options: IkCsvWriteOptions = {}): string => {
        let Text: string = "";
        if (Value !== null && Value !== undefined) { Text = String(Value); }
        if (Options.GuardFormulas === true && Text.length > 0 && "=+-@\t\r".indexOf(Text.charAt(0)) >= 0) { Text = "'" + Text; }
        let Quoted: boolean = Options.AlwaysQuote === true || /["\r\n]/.test(Text) || Text.indexOf(Delimiter) >= 0;
        if (Options.QuoteAlso !== undefined) {
            for (const Char of Options.QuoteAlso) { if (Text.indexOf(Char) >= 0) { Quoted = true; } }
        }
        if (Quoted === false) { return Text; }
        return "\"" + Text.split("\"").join("\"\"") + "\"";
    };

    /** DOCUMENTED EXCEPTION: pure record writer; callers supply their own delimiter and terminator. */
    public static Line = (Cells: string[], Delimiter: string = ",", Options: IkCsvWriteOptions = {}, Eol: string = "\r\n"): string => {
        const Out: string[] = [];
        for (const Cell of Cells) { Out.push(kCsvCore.Cell(Cell, Delimiter, Options)); }
        return Out.join(Delimiter) + Eol;
    };

    /** DOCUMENTED EXCEPTION: pure record parser. Quoted delimiters, doubled quotes and embedded
     * newlines remain field data. A leading BOM is ignored; an unterminated quote is a real fault. */
    public static Rows = (Text: string, Delimiter: string = ","): string[][] => {
        if (Delimiter !== "," && Delimiter !== "\t") { throw new Error("CSV delimiter must be comma or tab."); }
        const Input: string = Text.replace(/^\uFEFF/, "");
        const Rows: string[][] = [];
        let Row: string[] = [];
        let Field: string = "";
        let Quoted: boolean = false;
        for (let I: number = 0; I < Input.length; I++) {
            const Char: string = Input[I];
            if (Quoted) {
                if (Char === '"') {
                    if (Input[I + 1] === '"') { Field += '"'; I++; }
                    else { Quoted = false; }
                } else { Field += Char; }
            } else if (Char === '"' && Field === "") { Quoted = true; }
            else if (Char === Delimiter) { Row.push(Field); Field = ""; }
            else if (Char === "\r" || Char === "\n") {
                Row.push(Field); Rows.push(Row); Row = []; Field = "";
                if (Char === "\r" && Input[I + 1] === "\n") { I++; }
            } else { Field += Char; }
        }
        if (Quoted) { throw new Error("CSV contains an unterminated quoted field."); }
        Row.push(Field); Rows.push(Row);
        return Rows;
    };

    /** DOCUMENTED EXCEPTION: pure import delimiter selection. A tab outside quotes in the first
     * record identifies TSV; quoted tabs stay CSV field data. Plain CSV defaults to comma. */
    public static DelimiterOf = (Text: string): string => {
        const Input: string = Text.replace(/^\uFEFF/, "");
        let Quoted: boolean = false;
        let AtFieldStart: boolean = true;
        for (let I: number = 0; I < Input.length; I++) {
            const Char: string = Input[I];
            if (Quoted) {
                if (Char === '"') {
                    if (Input[I + 1] === '"') { I++; }
                    else { Quoted = false; }
                }
            } else {
                if (Char === "\t") { return "\t"; }
                if (Char === "\r" || Char === "\n") { break; }
                if (Char === ",") { AtFieldStart = true; }
                else if (Char === '"' && AtFieldStart) { Quoted = true; AtFieldStart = false; }
                else { AtFieldStart = false; }
            }
        }
        return ",";
    };

    /** DOCUMENTED EXCEPTION: pure lenient line parser; trims fields, accepts quotes anywhere
     * and unterminated quotes. Callers own gates and native input faults. */
    public static LineFields = (Line: string): string[] => {
        const Result: string[] = [];
        let Current: string = "";
        let InQuotes: boolean = false;
        let I: number = 0;

        while (I < Line.length) {
            const Char: string = Line[I];
            let NextChar: string = "";
            if (I + 1 < Line.length) { NextChar = Line[I + 1]; }

            if (InQuotes) {
                if (Char === '"') {
                    if (NextChar === '"') { Current += '"'; I += 2; continue; }
                    else { InQuotes = false; I++; continue; }
                } else { Current += Char; I++; }
            } else {
                if (Char === '"') { InQuotes = true; I++; }
                else if (Char === ",") { Result.push(Current.trim()); Current = ""; I++; }
                else { Current += Char; I++; }
            }
        }

        Result.push(Current.trim());
        return Result;
    };
}
